/* ==========================================================================
   VRINDAVAN FABRICS — CORE INTERACTION ENGINE
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {

  // ------------------------------------------------------------------------
  // 1. Loader Animation Manager
  // ------------------------------------------------------------------------
  const loader = document.getElementById('loader');
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  // Define loader fade out function
  const deactivateLoader = () => {
    if (!loader) return;
    loader.classList.add('fade-out');
    setTimeout(() => {
      loader.classList.add('hidden');
      checkScrollReveal();
      setTimeout(animateStatsBanner, 100);
    }, 500);
  };

  // Run loader — fast 1.2s cinematic feather presentation
  if (prefersReduced) {
    setTimeout(deactivateLoader, 300);
  } else {
    const loaderTimeout = setTimeout(deactivateLoader, 1200);
    window.addEventListener('load', () => {
      const timeSpent = performance.now();
      const minDuration = 1200;
      if (timeSpent < minDuration) {
        setTimeout(deactivateLoader, minDuration - timeSpent);
        clearTimeout(loaderTimeout);
      } else {
        deactivateLoader();
        clearTimeout(loaderTimeout);
      }
    });
  }

  // ------------------------------------------------------------------------
  // 2. SPA Client-Side Routing
  // ------------------------------------------------------------------------
  const pages = document.querySelectorAll('.page-view');
  const navLinks = document.querySelectorAll('.nav-link');
  const mobileNavLinks = document.querySelectorAll('.mobile-nav-link');

  let isInitialLoad = true;

  const router = () => {
    let rawHash = window.location.hash || '#/';
    
    // On fresh website load or refresh, always open on Home page (#/)
    if (isInitialLoad) {
      if (window.location.hash && window.location.hash !== '#/') {
        history.replaceState(null, '', window.location.pathname + '#/');
      }
      rawHash = '#/';
      isInitialLoad = false;
    }

    let route = rawHash.replace('#', '');
    if (route === '') route = '/';
    let routePath = route.split('?')[0];

    // Route map
    const routeMap = {
      '/': 'home',
      '/about': 'about',
      '/collections': 'collections',
      '/craftsmanship': 'craftsmanship',
      '/contact': 'contact'
    };

    const targetPageId = 'page-' + (routeMap[routePath] || 'home');
    const targetLinkData = routeMap[routePath] || 'home';

    if (routePath === '/collections') {
      const catMatch = rawHash.match(/cat=([a-z0-9_-]+)/i);
      const subMatch = rawHash.match(/sub=([a-z0-9_-]+)/i);
      const category = catMatch ? catMatch[1] : 'all';
      const subcategory = subMatch ? subMatch[1] : 'all';
      setTimeout(() => {
        if (typeof window.applyCategoryFilter === 'function') {
          window.applyCategoryFilter(category, subcategory);
        }
      }, 50);
    }

    // 1. Page transition effect
    const activePage = document.querySelector('.page-view:not(.hidden)');
    const targetPage = document.getElementById(targetPageId);

    if (activePage && activePage !== targetPage) {
      activePage.classList.add('fade-out');

      setTimeout(() => {
        // Hide previous page
        activePage.classList.add('hidden');
        activePage.classList.remove('fade-out');

        // Setup next page
        targetPage.classList.remove('hidden');
        targetPage.classList.add('fade-in');
        window.scrollTo({ top: 0, behavior: 'instant' });

        setTimeout(() => {
          targetPage.classList.remove('fade-in');
          // Re-check scroll reveals on new page load
          checkScrollReveal();
        }, 50);
      }, 400);
    } else if (targetPage) {
      // First load behavior
      pages.forEach(p => p.classList.add('hidden'));
      targetPage.classList.remove('hidden');
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    // 2. Update navigation active state
    navLinks.forEach(link => {
      if (link.getAttribute('data-page') === targetLinkData) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });

    mobileNavLinks.forEach(link => {
      if (link.getAttribute('data-page') === targetLinkData) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  };

  // Bind router events
  window.addEventListener('hashchange', router);
  router(); // Initialize router on start

  // ------------------------------------------------------------------------
  // 3. Permanent Fixed Header — Always Visible Scroll Engine
  // ------------------------------------------------------------------------
  const header = document.querySelector('.site-header');

  const updateHeaderScroll = () => {
    if (window.scrollY > 20) {
      header?.classList.add('scrolled');
    } else {
      header?.classList.remove('scrolled');
    }
  };

  window.addEventListener('scroll', updateHeaderScroll, { passive: true });
  updateHeaderScroll(); // Run initial state check

  // ------------------------------------------------------------------------
  // 4. Mobile Menu Drawer Navigation
  // ------------------------------------------------------------------------
  const menuToggle = document.getElementById('menu-toggle');
  const menuClose = document.getElementById('menu-close');
  const mobileMenu = document.getElementById('mobile-menu');
  const mobileMenuBackdrop = mobileMenu?.querySelector('.mobile-menu-backdrop');

  const openMobileMenu = () => {
    mobileMenu.classList.add('open');
    document.body.style.overflow = 'hidden'; // Stop page scrolling behind drawer
  };

  const closeMobileMenu = () => {
    mobileMenu.classList.remove('open');
    document.body.style.overflow = '';
  };

  menuToggle?.addEventListener('click', openMobileMenu);
  menuClose?.addEventListener('click', closeMobileMenu);
  mobileMenuBackdrop?.addEventListener('click', closeMobileMenu);

  // Close drawer when clicking links
  mobileNavLinks.forEach(link => {
    link.addEventListener('click', closeMobileMenu);
  });

  // ------------------------------------------------------------------------
  // 5. Scroll-Triggered Reveal Engine
  // ------------------------------------------------------------------------
  let revealElements = document.querySelectorAll('.reveal');

  // ------------------------------------------------------------------------
  // 5b. Statistics Count-Up Animation Engine
  // ------------------------------------------------------------------------
  const animateStatsBanner = () => {
    const loaderEl = document.getElementById('loader');
    // If the full-screen loader overlay is still visible, wait until it fades out
    if (loaderEl && !loaderEl.classList.contains('hidden')) return;

    const statItems = document.querySelectorAll('.stats-banner-section .stat-item');
    if (!statItems.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const item = entry.target;
            if (item.getAttribute('data-animated') === 'true') return;
            item.setAttribute('data-animated', 'true');

            const el = item.querySelector('.stat-number');
            if (!el) return;

            const targetAttr = el.getAttribute('data-count');
            if (!targetAttr || isNaN(parseFloat(targetAttr))) {
              const text = el.getAttribute('data-text');
              if (text) el.textContent = text;
              return;
            }

            const target = parseFloat(targetAttr);
            const suffix = el.getAttribute('data-suffix') || '';
            const decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
            const hasStar = el.getAttribute('data-star') === 'true';

            // Reset start text to 0 before counting
            if (decimals > 0) {
              el.innerHTML = `0.${'0'.repeat(decimals)}${suffix} ${hasStar ? '<span class="stat-star-accent">&starf;</span>' : ''}`;
            } else {
              el.innerHTML = `0${suffix}`;
            }

            let startTime = null;
            const duration = 2200; // 2.2 seconds smooth count-up

            const step = (timestamp) => {
              if (!startTime) startTime = timestamp;
              const progress = Math.min((timestamp - startTime) / duration, 1);
              
              // Custom Ease-Out Expo curve for dramatic counting decelerating at the end
              const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
              const currentVal = target * easeProgress;

              if (decimals > 0) {
                el.innerHTML = `${currentVal.toFixed(decimals)}${suffix} ${hasStar ? '<span class="stat-star-accent">&starf;</span>' : ''}`;
              } else {
                el.innerHTML = `${Math.floor(currentVal)}${suffix}`;
              }

              if (progress < 1) {
                requestAnimationFrame(step);
              } else {
                if (decimals > 0) {
                  el.innerHTML = `${target.toFixed(decimals)}${suffix} ${hasStar ? '<span class="stat-star-accent">&starf;</span>' : ''}`;
                } else {
                  el.innerHTML = `${target}${suffix}`;
                }
              }
            };

            // Small delay matching the stagger delay of the card
            const delayStr = item.style.transitionDelay || '0ms';
            const delayMs = parseInt(delayStr, 10) || 0;

            setTimeout(() => {
              requestAnimationFrame(step);
            }, delayMs);

            observer.unobserve(item);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -20px 0px' });

      statItems.forEach(item => observer.observe(item));
    }
  };

  // GSM Spectrum Numerical Count-up Animation
  const animateGsmSpectrum = () => {
    const gsmStrip = document.querySelector('.gsm-spec-strip');
    if (!gsmStrip) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const strip = entry.target;
            if (strip.getAttribute('data-animated') === 'true') return;
            strip.setAttribute('data-animated', 'true');

            const rangeElements = strip.querySelectorAll('.gsm-range-val');
            rangeElements.forEach(el => {
              const target = parseInt(el.getAttribute('data-count'), 10);
              if (isNaN(target)) return;

              const rawText = el.textContent || '';
              const isPlus = rawText.includes('+');
              let startTime = null;
              const duration = 1800;

              const step = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const progress = Math.min((timestamp - startTime) / duration, 1);
                const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
                const currentVal = Math.floor(target * ease);

                el.textContent = `${currentVal}${isPlus && progress === 1 ? '+' : ''}`;

                if (progress < 1) {
                  requestAnimationFrame(step);
                }
              };
              requestAnimationFrame(step);
            });

            observer.unobserve(strip);
          }
        });
      }, { threshold: 0.15 });

      observer.observe(gsmStrip);
    }
  };

  const checkScrollReveal = () => {
    // Refresh list of reveals (since router switches pages)
    revealElements = document.querySelectorAll('.reveal');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            // Unobserve once revealed to keep it locked
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      revealElements.forEach(el => observer.observe(el));
    } else {
      // Fallback for older browsers
      revealElements.forEach(el => el.classList.add('revealed'));
    }

    // Trigger stats and GSM animation if in view
    animateStatsBanner();
    animateGsmSpectrum();
  };

  // Listen to scrolls
  window.addEventListener('scroll', checkScrollReveal);

  // ------------------------------------------------------------------------
  // 6. Interactive Mouse-Parallax on Hero
  // ------------------------------------------------------------------------
  const heroSection = document.querySelector('.hero');
  const parallaxImg = document.querySelector('.parallax-img');

  if (heroSection && parallaxImg && !prefersReduced) {
    heroSection.addEventListener('mousemove', (e) => {
      const { width, height } = heroSection.getBoundingClientRect();
      const xVal = (e.clientX / width - 0.5) * 20; // Max 10px shift
      const yVal = (e.clientY / height - 0.5) * 20;

      parallaxImg.style.transform = `translate3d(${xVal}px, ${yVal}px, 0) scale(1.02)`;
    });

    heroSection.addEventListener('mouseleave', () => {
      parallaxImg.style.transform = 'translate3d(0, 0, 0) scale(1)';
    });
  }

  // ------------------------------------------------------------------------
  // 7. Collections Category & Subcategory Filter Engine
  // ------------------------------------------------------------------------
  const filterButtons = document.querySelectorAll('.filter-btn');
  const subcatContainer = document.getElementById('subcategory-filters');
  const subcatGroups = document.querySelectorAll('.subcat-group');
  const subfilterButtons = document.querySelectorAll('.subfilter-btn');
  const fabricCards = document.querySelectorAll('.fabric-card');

  let currentCategory = 'all';
  let currentSubcategory = 'all';

  const applyCategoryFilter = (catValue = 'all', subcatValue = 'all') => {
    currentCategory = catValue;
    currentSubcategory = subcatValue;

    // 1. Update primary category buttons active state
    filterButtons.forEach(b => {
      if (b.getAttribute('data-filter') === catValue) {
        b.classList.add('active');
      } else {
        b.classList.remove('active');
      }
    });

    // 2. Manage subcategory bar & active group
    let hasActiveSubcatGroup = false;
    subcatGroups.forEach(group => {
      const forCat = group.getAttribute('data-for-category');
      if (catValue === forCat) {
        group.classList.remove('hidden');
        hasActiveSubcatGroup = true;
      } else {
        group.classList.add('hidden');
      }
    });

    if (hasActiveSubcatGroup) {
      subcatContainer?.classList.remove('hidden');
    } else {
      subcatContainer?.classList.add('hidden');
    }

    // 3. Update active state on subcategory buttons in the active group
    subfilterButtons.forEach(btn => {
      const parentGroup = btn.closest('.subcat-group');
      const isGroupVisible = parentGroup && !parentGroup.classList.contains('hidden');

      if (isGroupVisible && btn.getAttribute('data-subfilter') === subcatValue) {
        btn.classList.add('active');
      } else if (!isGroupVisible || btn.getAttribute('data-subfilter') !== subcatValue) {
        btn.classList.remove('active');
        if (isGroupVisible && subcatValue === 'all' && btn.getAttribute('data-subfilter') === 'all') {
          btn.classList.add('active');
        }
      }
    });

    // 4. Filter fabric cards based on Category and Subcategory
    fabricCards.forEach(card => {
      const cardCat = card.getAttribute('data-category');
      const cardSub = card.getAttribute('data-subcategory');

      const matchesCat = (catValue === 'all' || cardCat === catValue);
      const matchesSub = (subcatValue === 'all' || cardSub === subcatValue);

      if (matchesCat && matchesSub) {
        card.classList.remove('hidden');
        card.classList.remove('fade-out');
      } else {
        card.classList.add('hidden');
      }
    });
  };

  // Expose function globally for routing
  window.applyCategoryFilter = applyCategoryFilter;

  // Primary category button click events
  filterButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const filterValue = btn.getAttribute('data-filter');
      applyCategoryFilter(filterValue, 'all');
    });
  });

  // Subcategory button click events
  subfilterButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const subValue = btn.getAttribute('data-subfilter');
      applyCategoryFilter(currentCategory, subValue);
    });
  });

  // ------------------------------------------------------------------------
  // 8. Contact Form Pre-filled WhatsApp Generator
  // ------------------------------------------------------------------------
  const enquiryForm = document.getElementById('enquiry-form');
  const successState = document.getElementById('contact-success-state');
  const whatsappSuccessBtn = document.getElementById('whatsapp-success-btn');

  enquiryForm?.addEventListener('submit', (e) => {
    e.preventDefault();

    const name = document.getElementById('form-name').value;
    const phone = document.getElementById('form-phone').value;
    const message = document.getElementById('form-message').value;

    // Send email to official email address fabricsvrindavan@gmail.com
    const emailSubject = encodeURIComponent(`New Fabric Enquiry from ${name}`);
    const emailBody = encodeURIComponent(`Hello Vrindavan Fabrics,\n\nI would like to submit an enquiry.\n\nName: ${name}\nPhone/WhatsApp: ${phone}\n\nEnquiry / Requirements:\n${message}\n\nThank you.`);
    const mailtoUrl = `mailto:fabricsvrindavan@gmail.com?subject=${emailSubject}&body=${emailBody}`;
    
    // Trigger mail client launch
    window.location.href = mailtoUrl;

    const encodedText = encodeURIComponent(`Hi Vrindavan Fabrics, my name is ${name}. I would like to enquiry about: ${message}. My contact number is ${phone}.`);

    // Direct WhatsApp URLs for all 3 representatives
    const urlMayank = `https://wa.me/917357190271?text=${encodedText}`;
    const urlSubham = `https://wa.me/919181257656?text=${encodedText}`;
    const urlVinod = `https://wa.me/919983345864?text=${encodedText}`;

    // Update all 3 button links dynamically
    document.getElementById('wa-btn-mayank')?.setAttribute('href', urlMayank);
    document.getElementById('wa-btn-subham')?.setAttribute('href', urlSubham);
    document.getElementById('wa-btn-vinod')?.setAttribute('href', urlVinod);

    // Toggle success state view
    enquiryForm.classList.add('hidden');
    successState.classList.remove('hidden');
  });

  // ------------------------------------------------------------------------
  // 9. Floating WhatsApp Speed-Dial Widget Interaction
  // ------------------------------------------------------------------------
  const waTrigger = document.getElementById('wa-float-trigger');
  const waPopup = document.getElementById('wa-contact-popup');

  if (waTrigger && waPopup) {
    waTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      waPopup.classList.toggle('active');
    });

    document.addEventListener('click', (e) => {
      if (!waPopup.contains(e.target) && !waTrigger.contains(e.target)) {
        waPopup.classList.remove('active');
      }
    });
  }

});

/* ==========================================================================
   PEACOCK FEATHER SPINE — S-curve barb & ocellus generator
   Runs when the Craftsmanship page is shown; re-runs on resize.
   ========================================================================== */
(function () {

  // ── Bezier helpers ─────────────────────────────────────────────────────────
  // Two half-segments that form the complete S-curve (viewBox: 0 0 80 1000)
  const SEGS = [
    { p0: [40, 20], p1: [75, 190], p2: [5, 370], p3: [40, 500] },
    { p0: [40, 500], p1: [75, 660], p2: [5, 820], p3: [40, 960] }
  ];

  function bp(s, t) {                // point on cubic bezier at t
    const u = 1 - t;
    return [
      u * u * u * s.p0[0] + 3 * u * u * t * s.p1[0] + 3 * u * t * t * s.p2[0] + t * t * t * s.p3[0],
      u * u * u * s.p0[1] + 3 * u * u * t * s.p1[1] + 3 * u * t * t * s.p2[1] + t * t * t * s.p3[1]
    ];
  }

  function bt(s, t) {                // tangent (derivative) at t
    const u = 1 - t;
    return [
      3 * (u * u * (s.p1[0] - s.p0[0]) + 2 * u * t * (s.p2[0] - s.p1[0]) + t * t * (s.p3[0] - s.p2[0])),
      3 * (u * u * (s.p1[1] - s.p0[1]) + 2 * u * t * (s.p2[1] - s.p1[1]) + t * t * (s.p3[1] - s.p2[1]))
    ];
  }

  // Binary-search for the t value on a segment whose y ≈ targetY
  function tForY(seg, targetY) {
    let lo = 0, hi = 1;
    for (let i = 0; i < 22; i++) {
      const mid = (lo + hi) * 0.5;
      bp(seg, mid)[1] < targetY ? (lo = mid) : (hi = mid);
    }
    return (lo + hi) * 0.5;
  }

  // ── Ocellus SVG fragment (teardrop, iridescent) ───────────────────────────
  function makeOcellus(cx, cy) {
    const R = [12, 9.5, 6.8, 4.2, 2.2];   // outer → inner ring radii
    // Slightly taller ellipses (ry = rx * 1.22) for a natural eye shape
    const shapes = [
      `<ellipse cx="${cx}" cy="${cy}" rx="${R[0]}" ry="${(R[0] * 1.22).toFixed(1)}"
        fill="none" stroke="#C79A3C" stroke-width="0.7" opacity="0.38"/>`,
      `<ellipse cx="${cx}" cy="${cy}" rx="${R[1]}" ry="${(R[1] * 1.22).toFixed(1)}"
        fill="url(#oGold)" opacity="0.72"/>`,
      `<ellipse cx="${cx}" cy="${cy}" rx="${R[2]}" ry="${(R[2] * 1.22).toFixed(1)}"
        fill="url(#oTeal)" stroke="#14B8A6" stroke-width="0.6" opacity="0.9"/>`,
      `<ellipse cx="${cx}" cy="${cy}" rx="${R[3]}" ry="${(R[3] * 1.22).toFixed(1)}"
        fill="#0B2F2C" opacity="0.92"/>`,
      `<ellipse cx="${cx}" cy="${cy}" rx="${R[4]}" ry="${(R[4] * 1.22).toFixed(1)}"
        fill="#090E10"/>`,
      // Shimmer highlight
      `<ellipse cx="${(cx - 1.4).toFixed(1)}" cy="${(cy - 2).toFixed(1)}"
        rx="2.2" ry="1.4" fill="white" opacity="0.42"
        transform="rotate(-28 ${(cx - 1.4).toFixed(1)} ${(cy - 2).toFixed(1)})"/>`,
      // Outer iridescent halo glow
      `<ellipse cx="${cx}" cy="${cy}" rx="${R[0] + 2.5}" ry="${((R[0] + 2.5) * 1.22).toFixed(1)}"
        fill="none" stroke="#14B8A6" stroke-width="1.2" opacity="0.14"
        filter="url(#og)"/>`
    ];
    return shapes.join('');
  }

  // ── Main builder ───────────────────────────────────────────────────────────
  function buildPeacockFeather() {
    const svgEl = document.getElementById('feather-spine-svg');
    const barbGroup = document.getElementById('feather-barbs');
    const ocGroup = document.getElementById('feather-ocelli');
    const wrapper = document.querySelector('.journey-timeline-wrapper');

    if (!svgEl || !barbGroup || !ocGroup || !wrapper) return;

    const wRect = wrapper.getBoundingClientRect();
    if (wRect.height < 10) return;   // wrapper not yet visible

    const VB_H = 1000;
    const ns = 'http://www.w3.org/2000/svg';

    // Clear previous runs
    barbGroup.innerHTML = '';
    ocGroup.innerHTML = '';

    // ── 1. Generate barbs along both segments ────────────────────────────────
    SEGS.forEach((seg, si) => {
      const STEPS = si === 0 ? 32 : 26;   // segment 1 denser (feather base nearer top)

      for (let i = 0; i <= STEPS; i++) {
        const t = i / STEPS;
        const [px, py] = bp(seg, t);
        const [tx, ty] = bt(seg, t);
        const len = Math.hypot(tx, ty);
        if (len < 0.001) continue;

        // Unit perpendicular vectors (left & right of travel direction)
        const pLx = -ty / len, pLy = tx / len;
        const pRx = ty / len, pRy = -tx / len;

        // Barb length profile: full near middle of each segment, shorter at ends
        // Segment 2 has shorter barbs overall (tapering toward calamus tip)
        const midSin = Math.sin(Math.PI * t);
        const L = si === 0
          ? 28 + 18 * midSin              // 28 → 46 → 28  for segment 1
          : 16 + 10 * midSin * (1 - t);   // shorter, tapering for segment 2

        // Slight natural upward curl at barb tips (toward feather tip = top of SVG)
        const curl = L * 0.07;

        // Left barb quadratic control & endpoint
        const lCpx = px + pLx * L * 0.42;
        const lCpy = py + pLy * L * 0.42 - curl * 0.5;
        const lEx = px + pLx * L;
        const lEy = py + pLy * L - curl;

        // Right barb
        const rCpx = px + pRx * L * 0.42;
        const rCpy = py + pRy * L * 0.42 - curl * 0.5;
        const rEx = px + pRx * L;
        const rEy = py + pRy * L - curl;

        // Stroke width: thicker at rachis, finer at tip
        const sw = si === 0
          ? (0.90 - 0.35 * t).toFixed(2)
          : (0.65 - 0.28 * t).toFixed(2);

        // Opacity: gentle taper in/out at segment edges + dimmer for seg 2
        const baseOp = si === 0 ? 0.82 : 0.58;
        const op = (baseOp * (0.28 + 0.72 * Math.sin(Math.PI * t * 0.92 + 0.06))).toFixed(2);

        const d = (x, y) => `${x.toFixed(1)},${y.toFixed(1)}`;

        // Draw both barbs
        [
          [`M ${d(px, py)} Q ${d(lCpx, lCpy)} ${d(lEx, lEy)}`, 'url(#bL)'],
          [`M ${d(px, py)} Q ${d(rCpx, rCpy)} ${d(rEx, rEy)}`, 'url(#bR)']
        ].forEach(([path, stroke]) => {
          const el = document.createElementNS(ns, 'path');
          el.setAttribute('d', path);
          el.setAttribute('stroke', stroke);
          el.setAttribute('stroke-width', sw);
          el.setAttribute('fill', 'none');
          el.setAttribute('opacity', op);
          el.setAttribute('filter', 'url(#fg)');
          barbGroup.appendChild(el);
        });
      }
    });

    // ── 2. Place ocellus at each timeline step, measured from DOM ────────────
    const steps = wrapper.querySelectorAll('.timeline-step');

    steps.forEach((step) => {
      const sRect = step.getBoundingClientRect();
      const centerY = sRect.top + sRect.height * 0.5 - wRect.top;

      // Convert screen y to viewBox y
      const vbY = (centerY / wRect.height) * VB_H;
      if (vbY < 0 || vbY > VB_H) return;

      // Find which segment and t-value
      const seg = vbY <= 500 ? SEGS[0] : SEGS[1];
      const t = tForY(seg, vbY);
      const [ocX, ocY] = bp(seg, t);

      // Inject teardrop ocellus SVG
      const g = document.createElementNS(ns, 'g');
      g.innerHTML = makeOcellus(ocX.toFixed(1), ocY.toFixed(1));
      ocGroup.appendChild(g);
    });
  }

  // ── Hook into router & resize ──────────────────────────────────────────────
  let resizeTimer;

  function tryBuild() {
    // Wait a tick for layout to settle after page show
    requestAnimationFrame(() => setTimeout(buildPeacockFeather, 80));
  }

  // Patch hashchange so we know when craftsmanship is shown
  window.addEventListener('hashchange', () => {
    const hash = window.location.hash || '#/';
    if (hash.includes('craftsmanship')) tryBuild();
  });

  // Handle initial load / direct navigation
  document.addEventListener('DOMContentLoaded', () => {
    const hash = window.location.hash || '#/';
    if (hash.includes('craftsmanship')) tryBuild();
    // Also fire when the page becomes un-hidden via the router's setTimeout
    // (router hides/shows after 400ms transition)
    const observer = new MutationObserver(() => {
      const page = document.getElementById('page-craftsmanship');
      if (page && !page.classList.contains('hidden')) {
        tryBuild();
      }
    });
    const craftPage = document.getElementById('page-craftsmanship');
    if (craftPage) observer.observe(craftPage, { attributes: true, attributeFilter: ['class'] });
  });

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(buildPeacockFeather, 220);
  });

})();

